/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useQuery } from '@tanstack/react-query';
import { LeaseService } from '../services/lease-service';
import { leasesKeys } from './leases-keys';

export interface LeaseDocument {
  document_id: string;
  filename: string;
  category: string;
  /** Free-text name, carried only by documents filed under `other`. */
  label?: string | null;
}

export interface LeaseDocuments {
  documents: LeaseDocument[];
  isLoading: boolean;
  isError: boolean;
  refetch: () => Promise<unknown>;
}

/**
 * The lease's attached papers.
 *
 * Both the header's tab badge and the Documentos tab itself need this
 * list. They share one query key on purpose -- react-query then serves
 * both from a single request instead of fetching the list twice.
 *
 * @param agencyId - The owning agency.
 * @param leaseId - The lease.
 * @returns The documents plus the flags the panel needs.
 */
export function useLeaseDocuments(agencyId: string, leaseId: string): LeaseDocuments {
  const query = useQuery({
    queryKey: leasesKeys.documents(agencyId, leaseId),
    queryFn: () => LeaseService.listDocuments(agencyId, leaseId),
    enabled: Boolean(agencyId && leaseId),
  });

  return {
    documents: query.data?.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: () => query.refetch(),
  };
}
